import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiPost } from "../lib/api";
import { ErrorState } from "./Loading";
import { FamilyAthlete } from "./PlanChooser";

function formatDate(iso: string): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? iso : date.toLocaleDateString(undefined, { month: "long", day: "numeric" });
}

/**
 * Stopping one athlete's training, from the family's side.
 *
 * The counterpart to the coach's version, and the more important of the two: a family with two skaters
 * who wanted to stop one had exactly one route, which was cancelling the whole subscription -- taking
 * their other child out with them. Cancelling remains the way to stop *everything*; this is how you stop
 * one person.
 *
 * Same terms as every other reduction in this app: it takes effect at the end of the month already paid
 * for, so nothing bought is taken back. Nothing is deleted either -- the plans, the history and the
 * conversations stay, which is what makes coming back in September a decision rather than a rebuild.
 */
export function StopTraining({ athlete }: { athlete: FamilyAthlete }) {
  const queryClient = useQueryClient();
  const [confirming, setConfirming] = useState(false);

  const change = useMutation({
    mutationFn: (action: "stop" | "resume") => apiPost(`/billing/athletes/${athlete.client_id}/${action}`),
    onSuccess: async () => {
      setConfirming(false);
      await queryClient.invalidateQueries({ queryKey: ["billing"] });
    },
  });

  // Only the side that stopped it can start it again -- a coach's decision is not the family's to undo,
  // and vice versa. Said plainly rather than shown as a button that fails.
  const stoppedByCoach = athlete.stopped_by === "coach";

  if (athlete.stopped) {
    return (
      <div className="plan-choice-block">
        {change.isError && <ErrorState error={change.error} />}
        <p className="form-hint">
          {stoppedByCoach
            ? `${athlete.name} isn't training with your coach at the moment. Everything they did is still here — message your coach if you'd like to start again.`
            : `${athlete.name} isn't training at the moment and isn't being billed. Everything they did is still here.`}
        </p>
        {!stoppedByCoach && (
          <button type="button" disabled={change.isPending} onClick={() => change.mutate("resume")}>
            {change.isPending ? "Starting…" : `Start ${athlete.name}'s training again`}
          </button>
        )}
      </div>
    );
  }

  if (athlete.stopping_at) {
    return (
      <div className="plan-choice-block">
        {change.isError && <ErrorState error={change.error} />}
        <p className="form-hint">
          {`${athlete.name} stops on ${formatDate(athlete.stopping_at)}. You're billed for them until then.`}
        </p>
        {!stoppedByCoach && (
          <button type="button" className="chip" disabled={change.isPending} onClick={() => change.mutate("resume")}>
            {change.isPending ? "Cancelling…" : "Keep them training"}
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="plan-choice-block">
      {change.isError && <ErrorState error={change.error} />}
      {!confirming ? (
        /* Labelled by consequence, not by verb. "Stop <name>'s training" sat directly beside "Cancel
           subscription" -- two similar-looking controls, one affecting one athlete and one affecting
           the whole family, with nothing on either to say which was which (UX review F31). */
        <button type="button" className="chip" onClick={() => setConfirming(true)}>
          Stop {athlete.name}&rsquo;s training only
        </button>
      ) : (
        <div className="confirm-box" role="group" aria-label="Confirm stopping training">
          <p>
            {`${athlete.name} comes off your bill at the end of the month you've already paid for. Their plans, sessions and messages all stay — you can start again any time, and your other athletes aren't affected.`}
          </p>
          <button type="button" disabled={change.isPending} onClick={() => change.mutate("stop")}>
            {change.isPending ? "Stopping…" : `Yes, stop ${athlete.name}'s training`}
          </button>
          <button type="button" className="chip" onClick={() => setConfirming(false)}>
            Never mind
          </button>
        </div>
      )}
    </div>
  );
}
